import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Save } from "lucide-react";
import BirthChartFormModern from "../../astro/birthchartform";
import ChartDrawer from "./ChartDrawer";

const BirthChartExperience = () => {
  const [page, setPage] = useState(0); // 0 = form, 1 = chart
  const [chartData, setChartData] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSaveChart = () => {
    setShowSaveModal(true);
  };

  const confirmSave = async () => {
    try {
      // Format the reading for journal
      const journalEntry = {
        userId: window.firebase.auth().currentUser?.uid || '',
        type: 'birth-chart',
        title: `Birth Chart - ${chartData.name || 'Unnamed'}`,
        content: `Birth Chart generated on ${new Date().toLocaleDateString()}`,
        details: {
          chartData: chartData
        },
        timestamp: new Date().toISOString()
      };
      
      // Save to localStorage for backup
      const savedReadings = JSON.parse(localStorage.getItem('birthChartSavedReadings') || '[]');
      const newReading = {
        id: Date.now(),
        date: new Date().toISOString(),
        chartData: chartData,
        type: 'birth-chart'
      };
      
      const updatedReadings = [...savedReadings, newReading];
      localStorage.setItem('birthChartSavedReadings', JSON.stringify(updatedReadings));
      
      // Save to Firebase journal if user is logged in
      if (window.firebase && window.firebase.auth && window.firebase.auth().currentUser) {
        const user = window.firebase.auth().currentUser;
        const entriesRef = window.firebase
          .firestore()
          .collection('journal')
          .doc(user.uid)
          .collection('entries');
        await entriesRef.add(journalEntry);
      }
      
      setSaveSuccess(true);
      
      // Auto-close modal after 2 seconds
      setTimeout(() => {
        setShowSaveModal(false);
        setSaveSuccess(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error saving chart:', error);
      alert('Error saving chart. Please try again.');
    }
  };

  return (
    <div>
      {page === 0 && (
        <BirthChartFormModern
          onChartReady={data => {
            setChartData(data);
            setPage(1);
          }}
        />
      )}
      {page === 1 && (
        <motion.div
          key="chart"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4 }}
          className="has-text-centered"
          style={{
            background: 'linear-gradient(135deg, #2d1b2e 0%, #4a2545 50%, #3d2438 100%)',
            minHeight: '100vh',
            padding: '4rem 1rem 2rem 1rem',
            position: 'relative'
          }}
        >
          {/* Buttons Container - Inside the background */}
          <div style={{
            position: 'relative',
            maxWidth: 600,
            margin: '0 auto 1rem',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '1rem',
            padding: '0 0.5rem'
          }}>
            {/* Back Button */}
            <motion.button
              className="button is-warning"
              style={{
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                borderRadius: "12px",
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontWeight: "600",
                border: "none",
                background: "linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)",
                color: "#000",
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              onClick={() => window.location.href = "/birthchart"}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 16px rgba(0,0,0,0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
              }}
            >
              <ArrowLeft size={20} />
              Back
            </motion.button>

            {/* Save Chart Button */}
            <button
              className="save-chart-button"
              onClick={handleSaveChart}
              style={{
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                borderRadius: "12px",
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontWeight: "600",
                border: "none",
                background: "linear-gradient(135deg, #E53E3E 0%, #C53030 100%)",
                color: "#fff",
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 16px rgba(229, 62, 62, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
              }}
            >
              <Save size={16} />
              Save Reading
            </button>
          </div>

          <div className="card modern-card" style={{
            maxWidth: 600,
            margin: "0 auto",
            padding: "2rem",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            background: "rgba(255, 255, 255, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "16px"
          }}>
            <ChartDrawer {...chartData} ascendantLongitude={chartData?.ascendantLongitude || 0} />
          </div>
        </motion.div>
      )}

      {/* Save to Journal Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={() => setShowSaveModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                color: '#FFD700',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 215, 0, 0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              ✕
            </button>

            {/* Modal Content */}
            <div style={{ textAlign: 'center' }}>
              {!saveSuccess ? (
                <>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    📖
                  </div>
                  
                  <h2 style={{
                    color: '#FFD700',
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '16px',
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Save to Journal
                  </h2>
                  
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '16px',
                    lineHeight: '1.6',
                    marginBottom: '24px'
                  }}>
                    Save your birth chart reading to your journal?
                  </p>
                </>
              ) : (
                <>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    ✅
                  </div>
                  
                  <h2 style={{
                    color: '#28a745',
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '16px'
                  }}>
                    Chart Saved!
                  </h2>
                  
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '16px',
                    lineHeight: '1.6'
                  }}>
                    Your birth chart has been successfully saved to your journal.
                  </p>
                </>
              )}

              {!saveSuccess && (
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => setShowSaveModal(false)}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      background: 'transparent',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = 'rgba(255, 215, 0, 0.5)';
                      e.target.style.color = '#FFD700';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    }}
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={confirmSave}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                      color: '#1a1a2e',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(255, 215, 0, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.3)';
                    }}
                  >
                    💾 Save to Journal
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BirthChartExperience; 
